import { useMemo } from 'react';

import { UploadedFile } from '@components/file-field';
import { FormLabel } from '@components/form-label';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { abbreviateDate } from '@utils/date';
import { formatNumber, hasNumericValue } from '@utils/numbers';

import { FormAnswerFieldType, FormAnswersV2, FormFile } from 'api/journey-action-forms/requests';

type ContentFileAnswer = Extract<FormAnswerFieldType, { kind: 'content-file' }>;

const toFormFile = (field: ContentFileAnswer): FormFile => ({
  name: field.fileName,
  src: field.url,
  srcConverted: field.urlConverted,
});

export const ResponseContentV2 = ({ formAnswers }: { formAnswers: FormAnswersV2 }) => {
  // A content-file the builder never attached a file to renders nothing in the
  // form itself, so it has nothing to show among the responses either.
  const fields = useMemo(
    () => formAnswers.filter((field) => field.kind !== 'content-file' || Boolean(field.url)),
    [formAnswers],
  );

  const formFiles = useMemo(() => {
    let formFiles: FormFile[] = [];

    fields.forEach((field) => {
      if (field.kind === 'input-file' && field.value) {
        formFiles = formFiles.concat(field.value);
      }
      if (field.kind === 'content-file') {
        formFiles.push(toFormFile(field));
      }
    });
    return formFiles;
  }, [fields]);

  return (
    <Stack space={6}>
      {fields.map((field) => (
        <RenderFieldContent key={field.id} field={field} files={formFiles} />
      ))}
    </Stack>
  );
};

const RenderFieldContent = ({
  field,
  files,
}: {
  field: FormAnswerFieldType;
  files: Array<FormFile>;
}) => {
  const showFilePreviewModal = usePreviewFileModal();
  const { t } = useScopedTranslate(locale);

  const fieldContent = (field: FormAnswerFieldType) => {
    const hasAnswer =
      'value' in field &&
      (typeof field.value === 'number' ||
        (typeof field.value === 'string' && field.value.trim().length > 0) ||
        (Array.isArray(field.value) && field.value.length > 0));

    const color: Color = hasAnswer ? 'textPrimary' : 'textHelper';

    switch (field.kind) {
      case 'content-file':
        return (
          <UploadedFile
            name={field.fileAlias || field.fileName}
            path={field.url}
            onPreview={() => {
              showFilePreviewModal({ formFile: toFormFile(field), filesList: files });
            }}
          />
        );
      case 'input-file':
        const hasValue = Array.isArray(field.value) && field.value.length > 0;
        return hasValue ? (
          <Stack space={2}>
            {field.value?.map((file, index) => (
              <UploadedFile
                key={index}
                name={file.name}
                path={file.src}
                onPreview={() => {
                  showFilePreviewModal({ formFile: file, filesList: files });
                }}
              />
            ))}
          </Stack>
        ) : (
          <Text font="regular14" color={color} text={t('noAnswer')} />
        );
      case 'separator':
        return <Separator />;
      case 'heading':
        return <Text font="heading2" text={field.label} />;
      case 'subheading':
        return <Text font="bold18" text={field.label} />;
      case 'input-text-small':
      case 'input-text-large':
      case 'input-phone':
      case 'input-kennitala':
        return <Text font="regular14" color={color} text={field.value || t('noAnswer')} />;
      case 'input-number':
        const formattedNumber =
          field.value !== null && hasNumericValue(field.value)
            ? formatNumber(field.value)
            : undefined;
        return (
          <Text
            font="regular14"
            color={color}
            text={hasAnswer ? (formattedNumber ?? t('noAnswer')) : t('noAnswer')}
          />
        );
      case 'input-email':
        return <Text font="regular14" color={color} text={field.value || t('noAnswer')} />;
      case 'input-single-choice':
        return (
          <Text
            font="regular14"
            color={color}
            text={
              field.options.find((option) => option.value === field.value)?.label || t('noAnswer')
            }
          />
        );
      case 'input-multiple-choice': {
        const matchedLabels = field.value
          ?.map((value) => field.options.find((option) => option.value === value)?.label)
          .filter((label): label is string => Boolean(label));
        return (
          <Text
            font="regular14"
            color={color}
            text={matchedLabels?.length ? matchedLabels.join(', ') : t('noAnswer')}
          />
        );
      }
      case 'input-date':
        return (
          <Text
            font="regular14"
            color={color}
            text={field.value ? abbreviateDate(field.value, 'en', 'datetime') : t('noAnswer')}
          />
        );
      case 'input-dropdown':
        return (
          <Text
            font="regular14"
            color={color}
            text={
              field.options.find((option) => option.value === field.value)?.label || t('noAnswer')
            }
          />
        );
    }

    return null;
  };

  const content = fieldContent(field);

  if (field.kind !== 'heading' && field.kind !== 'subheading' && 'label' in field) {
    return (
      <div>
        <FormLabel text={field.label} />
        {content}
      </div>
    );
  }
  return content;
};

const locale = {
  en: {
    noAnswer: 'No answer provided',
  },
};
